<template>
    <!-- Desktop: show search -->
    <div class="hidden lg:block">
        <SearchForm />
    </div>

    <!-- Mobile: open modal -->
    <div class="fixed bottom-0 left-0 w-full z-10 bg-white p-4 lg:hidden border-b border-gray-200 shadow-custom">
        <div class="flex gap-3 justify-center">
            <Button @click="openSearch" severity="primary" icon="icon-search">
                {{ $t('Search') }}
            </Button>

            <Button severity="secondary" icon="icon-filter" @click="openFilters">
                {{ $t('Filter') }}
            </Button>
        </div>
    </div>

    <!-- Mobile modal -->
    <Modal ref="searchModal" @close="closeSearch">
        <template #header>
            {{ $t('Search') }}
        </template>

        <div class="search-modal-content">
            <SearchForm />
        </div>
    </Modal>

    <Modal ref="filterModal">
        <template #header>
            {{ $t('Filter') }}
        </template>

        <div class="space-y-6">
            <div class="flex items-baseline justify-between">
                <div class="text-sm text-gray-600">
                    {{ $t('Total results: :total', { total }) }}
                </div>

                <Button v-if="enableResetFilter" @click="store.resetFilters" size="sm" severity="link" icon="icon-close">{{ $t('Reset filters') }}</Button>
            </div>

            <Filter v-for="filter in filters" :key="filter.name" :filter="filter" v-model="params.filter[filter.name]" />
        </div>

        <template #footer>
            <Button class="w-full" severity="primary" size="md" @click="closeFilters">
                {{ $t('Apply filters') }}
            </Button>
        </template>
    </Modal>

    <div class="flex gap-6 md:mt-12">
        <div class="w-1/4 hidden lg:block">
            <div class="flex items-baseline justify-between">
                <div>
                    <div class="text-lg font-semibold text-gray-800">
                        {{ $t('Filter') }}
                    </div>
                    <div class="text-gray-600 text-sm">{{ $t('Total results: :total', { total: total }) }}</div>
                </div>
                <Button v-if="enableResetFilter" @click="store.resetFilters" size="sm" severity="link" icon="icon-close">{{ $t('Reset filters') }}</Button>
            </div>
            <div class="space-y-6 mt-4">
                <template v-for="filter in filters" :key="filter.name">
                    <Filter :filter="filter" v-model="params.filter[filter.name]" />
                </template>
            </div>

            <!-- Filters would go here -->
        </div>
        <div class="w-full lg:w-3/4 lg:pl-6">
            <div class="space-y-4">
                <div class="flex md:items-center md:justify-between md:flex-row flex-col gap-4">
                    <h1 class="text-2xl font-semibold text-gray-800">{{ $t('List of offers for') }} {{ heading }}</h1>
                    <Sort v-model="params.sort" />
                </div>

                <div class="space-y-4 relative">
                    <Loading v-if="loading.items" />
                    <template v-for="item in items">
                        <ProductItem :item="item" :productContextQuery="productContextQuery" />
                    </template>
                    <template v-if="items.length === 0">
                        <div class="text-center text-gray-500 py-16">{{ $t('No products found.') }}</div>
                    </template>
                </div>
            </div>
            <div class="text-center my-8">
                <Button v-if="params.page < lastPage" @click="store.loadMore" size="lg" severity="primary" :loading="loading.loadMore">{{ $t('Load More') }}</Button>
            </div>
        </div>
    </div>
</template>

<script setup>
import SearchForm from '@/components/shared/search-form/SearchForm.vue'
import Filter from './_partials/Filter.vue'
import Sort from './_partials/Sort.vue'

import { useProductsStore } from '@/stores/products'
import { storeToRefs } from 'pinia'
import { pickProductContextQuery } from '@/composables/searchQuery'

const store = useProductsStore()

// Extract reactive refs
const { params, items, filters, loading, lastPage, total, enableResetFilter, context } = storeToRefs(store)

const searchModal = ref(null)
const filterModal = ref(null)

const route = useRoute()
const productContextQuery = computed(() => pickProductContextQuery(route.query))

// On the /nastanitve SEO listing route the location is implicit in the URL
// path (no `q` query param), so fall back to the resolved geo context.
const heading = computed(() => context.value?.label ?? params.value.q)

const openSearch = () => searchModal.value?.openModal()
const closeSearch = () => searchModal.value?.closeModal()

const openFilters = () => filterModal.value?.openModal()
const closeFilters = () => filterModal.value?.closeModal()

await store.initialize()
</script>
